import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';

import { SECURE_API } from 'src/api/api';
import MapWheelZoomHandler from 'src/common/components/leaflet/MapWheelZoomHandler';
import useDebouncedValue from 'src/hooks/useDebouncedValue';
import { newPageBlockId } from 'src/helpers/pageBlockFactory';
import { IMapBlock, IMapPin } from 'src/interfaces/PageBlock';

interface GeocodeSearchResult {
  displayName: string;
  lat: number;
  lng: number;
}

function MapClickCapture({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: event => {
      onClick(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

function MapEditorViewUpdater({
  center,
  zoom,
  height,
}: {
  center: [number, number];
  zoom: number;
  height: number;
}) {
  const map = useMap();
  const centerLat = center[0];
  const centerLng = center[1];

  useEffect(() => {
    map.setView([centerLat, centerLng], zoom, { animate: false });
  }, [map, centerLat, centerLng, zoom]);

  useEffect(() => {
    map.invalidateSize();
  }, [map, height]);

  return null;
}

function MapScrollWheelZoomToggle({ enabled }: { enabled: boolean }) {
  const map = useMap();

  useEffect(() => {
    if (enabled) {
      map.scrollWheelZoom.enable();
    } else {
      map.scrollWheelZoom.disable();
    }
  }, [map, enabled]);

  return null;
}

interface MapBlockEditorPanelProps {
  mapBlock: IMapBlock;
  onUpdate: (patch: Partial<IMapBlock>) => void;
}

export default function MapBlockEditorPanel({ mapBlock, onUpdate }: MapBlockEditorPanelProps) {
  const { t } = useTranslation();
  const pins = mapBlock.pins ?? [];
  const center = mapBlock.center ?? [46.586922, 25.082857];
  const zoom = mapBlock.zoom ?? 13;
  const height = mapBlock.height ?? 320;

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery.trim(), 1200);
  const [searchResult, setSearchResult] = useState<GeocodeSearchResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [mapHovered, setMapHovered] = useState(false);

  useEffect(() => {
    if (debouncedQuery.length < 3) {
      setSearchResult(null);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);

    SECURE_API.get<GeocodeSearchResult | null>('geocode/search', {
      params: { q: debouncedQuery },
    })
      .then(({ data }) => {
        if (!cancelled) {
          setSearchResult(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSearchResult(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSearchLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const applySearchResult = (result: GeocodeSearchResult) => {
    const nextCenter: [number, number] = [result.lat, result.lng];
    const nextPins =
      pins.length > 0
        ? pins.map((pin, index) =>
            index === 0
              ? { ...pin, lat: result.lat, lng: result.lng, label: pin.label || result.displayName }
              : pin,
          )
        : [
            ...pins,
            {
              id: newPageBlockId(),
              lat: result.lat,
              lng: result.lng,
              label: result.displayName,
            },
          ];

    onUpdate({
      center: nextCenter,
      pins: nextPins,
    } as Partial<IMapBlock>);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 1 }}>
        <TextField
          size="small"
          label={t('system.pages.mapSearchLabel')}
          placeholder={t('system.pages.mapSearchPlaceholder')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          sx={{ flex: '1 1 200px', minWidth: 0 }}
          slotProps={{
            input: {
              endAdornment: searchLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null,
            },
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('system.pages.mapZoom')}
          value={zoom}
          onChange={e => onUpdate({ zoom: Number(e.target.value || 13) } as Partial<IMapBlock>)}
          sx={{ flex: '0 0 100px', width: 100 }}
        />
        <TextField
          size="small"
          type="number"
          label={t('system.pages.mapHeight')}
          value={height}
          onChange={e => onUpdate({ height: Number(e.target.value || 320) } as Partial<IMapBlock>)}
          sx={{ flex: '0 0 120px', width: 120 }}
        />
      </Box>
      {debouncedQuery.length >= 3 && !searchLoading && !searchResult ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('system.pages.mapSearchNoResults')}
        </Typography>
      ) : null}
      {searchResult ? (
        <Paper variant="outlined" sx={{ mb: 1 }}>
          <ListItemButton onClick={() => applySearchResult(searchResult)}>
            <ListItemText
              primary={searchResult.displayName}
              secondary={`${searchResult.lat.toFixed(5)}, ${searchResult.lng.toFixed(5)}`}
            />
          </ListItemButton>
        </Paper>
      ) : null}
      <Typography variant="caption" color="text.secondary">
        {t('system.pages.mapBlockHint')}
      </Typography>
      <Box
        sx={{ mt: 1, border: '1px solid', borderColor: 'divider' }}
        onMouseEnter={() => setMapHovered(true)}
        onMouseLeave={() => setMapHovered(false)}
        onFocus={() => setMapHovered(true)}
        onBlur={() => setMapHovered(false)}
      >
        <MapContainer
          center={[center[0], center[1]]}
          zoom={zoom}
          style={{ width: '100%', height: `${height}px` }}
          scrollWheelZoom={mapHovered}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapEditorViewUpdater center={[center[0], center[1]]} zoom={zoom} height={height} />
          <MapScrollWheelZoomToggle enabled={mapHovered} />
          {mapHovered ? <MapWheelZoomHandler /> : null}
          <MapClickCapture
            onClick={(lat, lng) =>
              onUpdate({
                center: mapBlock.center ?? [lat, lng],
                pins: [...pins, { id: newPageBlockId(), lat, lng }],
              } as Partial<IMapBlock>)
            }
          />
          {pins.map(pin => (
            <Marker
              key={pin.id}
              draggable
              position={[pin.lat, pin.lng]}
              eventHandlers={{
                dragend: e => {
                  const pos = e.target.getLatLng();
                  onUpdate({
                    pins: pins.map(p =>
                      p.id === pin.id ? { ...p, lat: pos.lat, lng: pos.lng } : p,
                    ),
                  } as Partial<IMapBlock>);
                },
              }}
            />
          ))}
        </MapContainer>
      </Box>
      <Box sx={{ mt: 1, display: 'grid', gap: 1 }}>
        {pins.map((pin: IMapPin, pinIndex: number) => (
          <Box key={pin.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label={t('system.pages.pinLabel')}
              value={pin.label ?? ''}
              onChange={e =>
                onUpdate({
                  pins: pins.map((p, idx) =>
                    idx === pinIndex ? { ...p, label: e.target.value } : p,
                  ),
                } as Partial<IMapBlock>)
              }
            />
            <TextField
              size="small"
              label="Lat"
              type="number"
              value={pin.lat}
              onChange={e =>
                onUpdate({
                  pins: pins.map((p, idx) =>
                    idx === pinIndex ? { ...p, lat: Number(e.target.value) } : p,
                  ),
                } as Partial<IMapBlock>)
              }
            />
            <TextField
              size="small"
              label="Lng"
              type="number"
              value={pin.lng}
              onChange={e =>
                onUpdate({
                  pins: pins.map((p, idx) =>
                    idx === pinIndex ? { ...p, lng: Number(e.target.value) } : p,
                  ),
                } as Partial<IMapBlock>)
              }
            />
            <IconButton
              size="small"
              onClick={() =>
                onUpdate({
                  pins: pins.filter(p => p.id !== pin.id),
                } as Partial<IMapBlock>)
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
